import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Button,
  List,
  ListItem,
  makeStyles,
  Typography,
  useTheme,
  Grid,
  Divider,
  useMediaQuery,
  Fab
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'

import { FormSkeleton, useNotify, AwaitedNotification } from '@globaleris/ui.library'
import {
  useConfirmationDialog,
  useErrorDetailListDialog,
  useFormDialog,
  useHttpError
} from '../../../hooks'
import { GlobalNotification } from '../../../model'
import Page, { PageLayout } from '../../../components/page/page'

import { ModifiedVersion, NewPriceVersion, PriceVersion } from '../pricing.Model'
import { fetchPriceVersions, recalculatePricesValues, saveVersionsBatch } from '../pricing.service'

import { AddVersionDialog } from './components'
import VersionCard from './components/versionCard/versionCard.component'

import { styles } from './versions.styles'
import { getActivePrice, getVersions, notifyError, storeVersions } from '../../../redux'
import { ErrorDetailList } from '../../../components/errorDetailList/errorDetailList'
import { enumKeyToValue } from '../../../helpers'
import moment from 'moment'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { sortPriceVersionsByStartDate, sortVersionsByStartDate } from '../pricing.mapper'
import { EnumPriceType } from '../../../api/oData/enums/enumPriceType'

interface formDependenciesProps {
  formState: Partial<PriceVersion>
}

interface versionsValidationModel {
  id: number
  isValid: boolean
}

export const VersionsPage: React.FC<any> = props => {
  const [t] = useTranslation(['versions', 'pricingDetails', 'globalerisLibrary', 'enums'])
  let history = useHistory()

  const { showError, clearError, errorProps } = useHttpError()
  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )
  const { showErrorDetailListDialog, errorDetailListDialogProps } = useErrorDetailListDialog()
  const [showAddVersionDialog, setShowAddVersionDialog] = useState<boolean>(false)

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const matchesSmallDevices = useMediaQuery(currentTheme.breakpoints.down('sm'))

  const price = getActivePrice()

  const { info, error, awaited } = useNotify()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [versions, setVersions] = useState<any[]>([])
  const [isFetching, setIsFetching] = useState<boolean>(true)
  const [expandedCard, setExpandedCard] = useState<number | null>(null)
  const [modifiedVersions, setModifiedVersions] = useState<ModifiedVersion[]>([])
  const [versionsValidation, setVersionsValidation] = useState<versionsValidationModel[]>([])

  // Handlers Region Starts

  const handleChange = (versionNumber: number | null) => {
    setExpandedCard(versionNumber)
  }

  const updateVersionsValidationList = (versionId: number, isValid: boolean) => {
    const versionsFiltered = versionsValidation.filter(ver => ver.id !== versionId)
    setVersionsValidation([...versionsFiltered, { id: versionId, isValid: isValid }])
  }

  const addVersionHandler = (newVersion: Partial<NewPriceVersion>) => {
    const modVersions = versions.map(vs => {
      if (vs.version !== newVersion.version) {
        return vs
      } else {
        return newVersion
      }
    })

    const versionsList = modVersions.map(vs => {
      return {
        ...vs,
        startDate: moment(vs.startDate).toDate(),
        endDate: vs.endDate ? moment(vs.endDate).toDate() : undefined
      }
    })
    const recalculatedVersionsResult = recalculateVersions(versionsList, newVersion)
    const recalculatedVersions = recalculatedVersionsResult[0].map(version => {
      return {
        ...version,
        startDate: version?.startDate?.toString(),
        endDate: version?.endDate ? version?.endDate.toString() : undefined
      }
    })

    let modList = modifiedVersions
    if (recalculatedVersionsResult[1].length > 0) {
      recalculatedVersionsResult[1].map(verToAdd => {
        const isEditedVsInModList = modifiedVersions.some(vs => {
          if (vs.method === 'PATCH' || vs.method === 'POST') {
            if (vs.version.id === verToAdd.version.id) {
              return true
            }
          }
          return false
        })
        const isVsToDeleteInList = modifiedVersions.some(vs => {
          if (vs.version.id === verToAdd.version.id && verToAdd.method === 'DELETE') {
            return true
          } else {
            return false
          }
        })
        if (isVsToDeleteInList) {
          modList = modList.map(vers => {
            if (vers.version.id === verToAdd.version.id) {
              return verToAdd
            } else {
              return vers
            }
          })
        } else if (isEditedVsInModList) {
          modList = modList.map(vers => {
            if (
              (vers.method === 'PATCH' || vers.method === 'POST') &&
              vers.version.id === verToAdd.version.id
            ) {
              return {
                version: { ...vers.version, ...verToAdd.version },
                method: vers.method
              }
            } else {
              return vers
            }
          })
        } else {
          modList.push({
            version: { id: verToAdd.id, ...verToAdd.version },
            method: verToAdd.method
          })
        }
      })
    }

    setModifiedVersions(modList)
    setVersions(recalculatedVersions)
  }

  const editVersionHandler = (editedVersionProp, originalVersionToEdit) => {
    const modVersions = versions.filter(vs => vs.version !== originalVersionToEdit.version)
    const modifiedVersion = {
      id: originalVersionToEdit.id,
      version: originalVersionToEdit.version,
      ...editedVersionProp
    }
    const editedVersion = {
      ...originalVersionToEdit,
      ...editedVersionProp
    }

    // If editedProp is not a date, then no need to recalculate version dates. We only update the edited version.
    const recalculateVersionsDates = Object.keys(editedVersionProp).some(
      key => key.toLowerCase() === 'startdate' || key.toLowerCase() === 'enddate'
    )

    if (!recalculateVersionsDates) {
      const versionsList = versions.map(vs => {
        if (vs.version !== originalVersionToEdit.version) {
          return vs
        } else {
          return originalVersionToEdit
        }
      })

      const isEditedVsInModList = modifiedVersions.some(vs => {
        if (vs.method === 'PATCH' || vs.method === 'POST') {
          if (vs.version.id === originalVersionToEdit.id) {
            return true
          }
        }
        return false
      })

      let modList = modifiedVersions
      if (isEditedVsInModList) {
        modList = modList.map(vers => {
          if (
            (vers.method === 'PATCH' && vers.version.id === originalVersionToEdit.id) ||
            (vers.method === 'POST' && vers.version.version === originalVersionToEdit.version)
          ) {
            return {
              version: { ...vers.version, ...editedVersionProp },
              method: vers.method
            }
          } else {
            return vers
          }
        })
      } else {
        modList.push({
          version: { id: originalVersionToEdit.id, ...editedVersionProp },
          method: 'PATCH'
        })
      }

      setModifiedVersions(modList)
      setVersions(versionsList)
    } else {
      const versionsList = modVersions.map(vs => {
        return {
          ...vs,
          startDate: moment(vs.startDate).toDate(),
          endDate: vs.endDate ? moment(vs.endDate).toDate() : undefined
        }
      })
      const recalculatedVersionsResult = recalculateVersions(
        versionsList,
        editedVersion,
        modifiedVersion
      )
      const recalculatedVersions = recalculatedVersionsResult[0].map(version => {
        return {
          ...version,
          startDate: version?.startDate?.toString(),
          endDate: version?.endDate
            ? version?.endDate.toString()
            : version.version?.endDate === null
              ? null
              : undefined
        }
      })

      const fixDates = recalculatedVersionsResult[1].map(version => {
        if (
          version.version?.startDate !== undefined &&
          version.version?.startDate !== null &&
          version.version?.endDate !== undefined &&
          version.version?.startDate !== null
        ) {
          return {
            method: version.method,
            version: {
              ...version.version,
              startDate: moment(version.version?.startDate).toDate(),
              endDate:
                version.version?.endDate && moment(version.version?.endDate).isValid()
                  ? moment(version.version?.endDate).toDate()
                  : version.version?.endDate === null
                    ? null
                    : undefined
            }
          }
        } else if (version.version?.startDate) {
          return {
            method: version.method,
            version: {
              ...version.version,
              startDate: moment(version.version?.startDate).toDate()
            }
          }
        } else if (version.version?.endDate) {
          return {
            method: version.method,
            version: {
              ...version.version,
              endDate: version.version?.endDate
                ? moment(version.version?.endDate).toDate()
                : version.version?.endDate === null
                  ? null
                  : undefined
            }
          }
        } else {
          return version
        }
      })

      let modList = modifiedVersions
      if (fixDates.length > 0) {
        fixDates.map(verToAdd => {
          const isEditedVsInModList = modifiedVersions.some(vs => {
            if (vs.method === 'PATCH' || vs.method === 'POST') {
              if (vs.version.id === verToAdd.version.id) {
                return true
              }
            }
            return false
          })
          const isVsToDeleteInList = modifiedVersions.some(vs => {
            if (vs.version.id === verToAdd.version.id && verToAdd.method === 'DELETE') {
              return true
            } else {
              return false
            }
          })
          if (isVsToDeleteInList) {
            modList = modList.map(vers => {
              if (vers.version.id === verToAdd.version.id) {
                return verToAdd
              } else {
                return vers
              }
            })
          } else if (isEditedVsInModList) {
            modList = modList.map(vers => {
              if (
                (vers.method === 'PATCH' || vers.method === 'POST') &&
                vers.version.id === verToAdd.version.id
              ) {
                return {
                  version: { ...vers.version, ...verToAdd.version },
                  method: vers.method
                }
              } else {
                return vers
              }
            })
          } else {
            modList.push({
              version: { id: verToAdd.id, ...verToAdd.version },
              method: verToAdd.method
            })
          }
        })
      }

      setModifiedVersions(modList)
      setVersions(recalculatedVersions)
    }
  }

  const deleteVersionHandler = (deletedVersion: NewPriceVersion) => {
    // const deletedVersion = versions.filter(vers => vers.id === deletedVersion.id)[0];
    let versionsToMod: any[] = modifiedVersions

    const versionsList = versions
      .map(vs => {
        if (vs.id !== deletedVersion.id) {
          return {
            ...vs,
            startDate: moment(vs.startDate).toDate(),
            endDate: vs.endDate ? moment(vs.endDate).toDate() : undefined
          }
        } else {
          // we soft delete (set to be deleted) the version requested.
          const deletedVersionIsInPendingList = versionsToMod.some(
            el => el.version.id === deletedVersion.id
          )
          if (deletedVersionIsInPendingList) {
            const list = versionsToMod
            versionsToMod = list
              .map(vers => {
                if (vers.version.id === deletedVersion.id) {
                  if (vers.method !== 'POST') {
                    return {
                      version: { id: vers.version.id },
                      method: 'DELETE'
                    }
                  } else {
                    // In case the version is set to 'POST', it means is a new version that is not commited yet. In that case, we just remove it from "versionsToMod".
                    return
                  }
                } else {
                  return vers
                }
              })
              .filter(el => el?.method)
            // Clean pending validation list.
            deletedVersion?.id && updateVersionsValidationList(deletedVersion.id, true)
          } else {
            versionsToMod.push({
              version: { id: deletedVersion.id },
              method: 'DELETE'
            })
          }
        }
      })
      .filter(el => el?.version)

    // Fix date gaps due to removing the deletedVersion.
    const fixGapsVersionsRes = fixGapsVersions(versionsList, deletedVersion)
    const fixedList = fixGapsVersionsRes[0]
    // Versions with modified dates to be update. We need to add it to versionsToMod.
    const toUpdateVs = fixGapsVersionsRes[1]
    const list = versionsToMod

    toUpdateVs.map(modVer => {
      if (list.length > 0) {
        const versionToUpdateIsInModList = list.some(el => el.version.id === modVer.version.id)
        if (versionToUpdateIsInModList) {
          // if its already set to be modified or create, we edit that obj. We remain the method the same.
          versionsToMod = versionsToMod.map(vers => {
            if (vers.version.id === modVer.version.id) {
              if (vers.method === 'POST' || vers.method === 'PATCH') {
                if (modVer.method === 'DELETE') {
                  return {
                    version: { id: modVer.id },
                    method: 'DELETE'
                  }
                } else {
                  return {
                    version: { ...vers.version, ...modVer.version },
                    method: vers.method
                  }
                }
              } else if (vers.method === 'DELETE') {
                return vers
              }
            } else {
              return vers
            }
          })
        } else {
          // if it's not in the to-be-modified list, we add it as a patch:
          versionsToMod.push({
            version: modVer.version,
            method: modVer.method
          })
        }
      } else {
        // if the list is empty (shouldnt be empty anyways since AT LEAST, it will have the deletedItem we just add above)
        versionsToMod.push({
          version: modVer.version,
          method: modVer.method
        })
      }
    })

    const recalculatedVersions = fixedList.map(version => {
      return {
        ...version,
        startDate: moment(version?.startDate).toDate(),
        endDate: version?.endDate ? moment(version?.endDate).toDate() : undefined
      }
    })

    // Fix versions numbers and set the modified ones.
    const toSaveList = versionsToMod
    const sortedVersions = sortVersionsByStartDate(recalculatedVersions).map((version, idx) => {
      // if version's version does not match the already sorted by date array, then we need to fix the version number and add it to the toSaveList.
      if (version.version !== idx + 1) {
        if (toSaveList.length > 0) {
          const versionToUpdateIsInModList = toSaveList.some(el => el.version.id === version.id)
          if (versionToUpdateIsInModList) {
            toSaveList.map(vs => {
              // if its already to be modified or create, we edit that obj (only).
              if (vs.version.id === version.id && vs.method !== 'DELETE') {
                versionsToMod = versionsToMod.map(vers => {
                  if (vers.version.id === vs.version.id) {
                    return { version: { ...vers.version, version: idx + 1 }, method: vers.method }
                  } else {
                    return vers
                  }
                })
              } else {
                return
              }
            })
          } else {
            // if it's not in the to-be-modified list, we add it as a patch:
            versionsToMod.push({
              version: { id: version.id, version: idx + 1 },
              method: 'PATCH'
            })
          }
        } else {
          // Again, this should never happen. The list should have AT LEAST the deletedItem.
          versionsToMod.push({ version: { id: version.id, version: idx + 1 }, method: 'PATCH' })
        }
      }
      return { ...version, version: idx + 1 }
    })

    setModifiedVersions(versionsToMod ? versionsToMod : [])
    setVersions(sortedVersions)
    info(`Se ha eliminado la Versión ${deletedVersion.version}`)
  }

  const discardChangesHandler = () => {
    getConfirmation(ConfirmationDialogs.DISCARD_CHANGES).then(async () => {
      setVersionsValidation([])
      setModifiedVersions([])
      getPriceVersions()
      closeConfirmation()
    })
  }

  const saveVersionsHandler = () => {
    getConfirmation(ConfirmationDialogs.SAVE_VERSIONS).then(async () => {
      savePriceVersions()
      closeConfirmation()
    })
  }

  const goBackHandler = () => {
    if (modifiedVersions && modifiedVersions?.length > 0) {
      getConfirmation(ConfirmationDialogs.BACK_VERSIONS).then(async () => {
        setModifiedVersions([])
        history.goBack()
        closeConfirmation()
      })
    } else {
      history.goBack()
    }
  }

  // Triggers Pricing Engine to recalculate price's values.
  const recalculateValues = async () => {
    if (price) {
      awaited({
        title: t('pricingDetails:notifications.recalculatePrices.title', {
          priceCode: price?.code
        }),
        message: t('pricingDetails:notifications.recalculatePrices.message'),
        keyInfo: price?.id.toString()
      } as AwaitedNotification)

      recalculatePricesValues(price?.id)
    }

    //Notifications ****************************************************************************************************/
    // const recalculate = (result: boolean, connectionId: string | null) => {
    //   if (result) {
    //     console.log(connectionId);
    //     price && recalculatePricesValues(price?.id, connectionId);
    //   } else {
    //     console.log(`No ha conectado, ${connectionId}`);
    //   }
    // };

    // awaited(
    //   t('pricingDetails:notifications.recalculatePrices.message'),
    //   t('pricingDetails:notifications.recalculatePrices.title', {
    //     priceCode: price?.code
    //   }),
    //   undefined,
    //   { onConnectedHandler: recalculate }
    // );

    //Notifications ****************************************************************************************************/
    // setIsLoading(false);
  }

  // Handlers Region Ends

  // Functions Region Starts

  // Function to be used when a version is deleted to fix gaps in dates.
  const fixGapsVersions = (newVersionsList: NewPriceVersion[], deletedVersion: NewPriceVersion) => {
    let versionsToUpdate: any[] = []
    let modVersion: any
    let versionToPatch: any
    // if (deletedVersion.id === newVersionsList[newVersionsList.length - 1].id) {
    const fixedList = newVersionsList.map((version, idx) => {
      const nextVersion = idx + 1 === newVersionsList.length ? null : newVersionsList[idx + 1]
      const previousVersion = idx > 0 ? newVersionsList[idx - 1] : null

      const isGapNext = nextVersion
        ? moment(version.endDate).diff(moment(nextVersion.startDate), 'days') !== -1
        : false
      const isGapPrev = previousVersion
        ? moment(version.startDate).diff(moment(previousVersion.endDate), 'days') !== 1
        : false

      if ((nextVersion === null && version.version === deletedVersion.version - 1) || isGapPrev) {
        versionToPatch = {
          version: {
            version: version.version,
            id: version.id
          },
          method: 'PATCH'
        }

        if (isGapPrev) {
          // Case 1: the version was somewhere in the middle.
          modVersion = { ...version, startDate: moment(deletedVersion.startDate).toDate() }
          versionToPatch = {
            ...versionToPatch,
            version: {
              ...versionToPatch.version,
              startDate: modVersion.startDate
            }
          }
        } else if (nextVersion === null) {
          // Case 2: the version was the last one.
          // It is asked to set the endDate to the deleted version's endDate (either a value or null date)
          modVersion = {
            ...version,
            endDate: deletedVersion.endDate ? moment(deletedVersion.endDate).toDate() : null
          }
          versionToPatch = {
            ...versionToPatch,
            version: { ...versionToPatch.version, endDate: modVersion.endDate }
          }
        }

        versionsToUpdate.push(versionToPatch)
        return modVersion
      }

      return version
    })

    return [fixedList, versionsToUpdate]
  }

  const recalculateVersions = (
    newVersionsList: NewPriceVersion[],
    newVersion: Partial<NewPriceVersion>,
    editedProps?: Partial<NewPriceVersion>
  ) => {
    const versionsList = sortVersionsByStartDate([...newVersionsList, newVersion])

    let versionsToMod: any[] = []
    let newVersionB: any

    if (newVersion.endDate) {
      // SCENARIOS WHERE THE NEW VERSION HAS A FINITE END DATE.
      const list = versionsList.flatMap((version, idx) => {
        if (version.version !== newVersion.version) {
          // Versions with date conflicts will be modified.
          if (version.endDate === undefined || version.endDate === null) {
            // ** Version "i" endDate is null/undefined (infinite). **
            // Case 1: Version "i" starts AFTER new Version endDate --> Version "i" remains the same.
            if (moment(version.startDate).isAfter(moment(newVersion.endDate), 'day')) {
              let previousVersion = idx > 0 ? versionsList[idx - 1] : null
              let nVersion = newVersion
              let tempPrev = previousVersion

              if (newVersionB) {
                previousVersion = newVersionB
                nVersion = newVersionB
              }

              const isGapBefore =
                moment(version.startDate).diff(moment(previousVersion?.endDate), 'days') > 1

              if (isGapBefore && previousVersion?.id === nVersion.id) {
                const modVersion = {
                  id: version.id,
                  version: version.version,
                  startDate: moment(nVersion.endDate).add(1, 'days').toDate()
                }
                versionsToMod.push({ version: modVersion, method: 'PATCH' })
                const modifiedVersion = { ...version, startDate: modVersion.startDate }
                if (newVersionB) {
                  previousVersion = tempPrev
                }
                newVersionB = null
                return modifiedVersion
              } else {
                if (newVersionB) {
                  previousVersion = tempPrev
                }
                newVersionB = null
                return version
              }
            }
            // Case 2: Version "i" starts AFTER (or same) new Version startDate and BEFORE (or same) new Version endDate --> Version "i" new startDate is when newVersion ends.
            else if (
              moment(version.startDate).isBetween(
                moment(newVersion.startDate),
                moment(newVersion.endDate),
                'days',
                '[]'
              )
            ) {
              const modVersion = {
                id: version.id,
                version: version.version,
                startDate: moment(newVersion.endDate).add(1, 'days').toDate()
              }
              versionsToMod.push({ version: modVersion, method: 'PATCH' })
              const modifiedVersion = { ...version, startDate: modVersion.startDate }
              return modifiedVersion
            }
            // Case 3: Version "i" starts BEFORE new Version starts --> Version "i" new endDate until newVersion starts.
            // Version needs to be splitted.
            else if (moment(version.startDate).isBefore(moment(newVersion.startDate), 'day')) {
              newVersionB = {
                ...version,
                id: Math.random(),
                startDate: moment(newVersion.endDate).add(1, 'days').toDate()
              }
              const modVersion = {
                id: version.id,
                version: version.version,
                endDate: moment(newVersion.startDate).subtract(1, 'days').toDate()
              }
              versionsToMod.push({ version: newVersionB, method: 'POST' })
              versionsToMod.push({ version: modVersion, method: 'PATCH' })
              setVersionsValidation(versionsValidation => [
                ...versionsValidation,
                { id: newVersionB.id, isValid: true }
              ])

              return [{ ...version, ...modVersion }, newVersionB]
            }
          } else {
            // ** Version "i" endDate is not null/undefined. **

            const nextVersion = idx + 1 === versionsList.length ? null : versionsList[idx + 1]
            let previousVersion = idx > 0 ? versionsList[idx - 1] : null

            // new Version is FULLY included in Version "i" dates range
            // --> Version "i" must be splitted in 2: Version_i_A < -> NewVersion < -> Version_i_B
            if (
              moment(version.startDate).isBefore(moment(newVersion.startDate), 'day') &&
              (moment(version.endDate).isAfter(moment(newVersion.endDate), 'day') ||
                version.endDate === null)
            ) {
              newVersionB = {
                ...version,
                id: Math.random(),
                startDate: moment(newVersion.endDate).add(1, 'days').toDate()
              }
              const modVersion = {
                id: version.id,
                version: version.version,
                endDate: moment(newVersion.startDate).subtract(1, 'days').toDate()
              }
              versionsToMod.push({ version: newVersionB, method: 'POST' })
              versionsToMod.push({ version: modVersion, method: 'PATCH' })
              setVersionsValidation(versionsValidation => [
                ...versionsValidation,
                { id: newVersionB.id, isValid: true }
              ])

              return [{ ...version, ...modVersion }, newVersionB]

              // Case 2: Version "i" is not overlapped with new Version's dates -->
              // We check if there are any gaps, otherwise Version "i" remains the same.
            } else if (
              version.endDate &&
              moment(version.endDate).isBefore(moment(newVersion.startDate), 'day')
            ) {
              const isGapAfter =
                moment(nextVersion?.startDate).diff(moment(version.endDate), 'days') > 1

              if (isGapAfter) {
                let modVersion: any
                if (nextVersion?.id === newVersion.id) {
                  modVersion = {
                    id: version.id,
                    version: version.version,
                    endDate: moment(newVersion.startDate).subtract(1, 'days').toDate()
                  }
                } else {
                  modVersion = {
                    id: version.id,
                    version: version.version,
                    endDate: moment(nextVersion?.startDate).subtract(1, 'days').toDate()
                  }
                }
                versionsToMod.push({ version: modVersion, method: 'PATCH' })
                const modifiedVersion = { ...version, endDate: modVersion.endDate }
                return modifiedVersion
              } else return version

              // Case 3: Version "i" starts after new Version endDate
              // We check if there are any gaps, otherwise Version "i" remains the same.
            } else if (moment(version.startDate).isAfter(moment(newVersion.endDate), 'day')) {
              // We check if we have previously splitted a version. In that case, we have to check gap against the newVersionB (2nd leg of the splitted version)

              let nVersion = newVersion
              let tempPrev = previousVersion
              if (newVersionB) {
                previousVersion = newVersionB
                nVersion = newVersionB
              }

              const isGapBefore =
                moment(version.startDate).diff(moment(previousVersion?.endDate), 'days') > 1

              if (isGapBefore && previousVersion?.id === nVersion.id) {
                const modVersion = {
                  id: version.id,
                  version: version.version,
                  startDate: moment(nVersion.endDate).add(1, 'days').toDate()
                }
                versionsToMod.push({ version: modVersion, method: 'PATCH' })
                const modifiedVersion = { ...version, startDate: modVersion.startDate }
                if (newVersionB) {
                  previousVersion = tempPrev
                }
                newVersionB = null
                return modifiedVersion
              } else {
                if (newVersionB) {
                  previousVersion = tempPrev
                }
                newVersionB = null
                return version
              }
              // Case 4: Version "i" starts before the new Version and ends before the new Version endDate.
            } else if (
              version.endDate &&
              moment(version.startDate).isBefore(moment(newVersion.startDate), 'day') &&
              moment(version.endDate).isSameOrAfter(moment(newVersion.startDate), 'day') &&
              moment(version.endDate).isBefore(moment(newVersion.endDate), 'day')
            ) {
              const modVersion = {
                id: version.id,
                version: version.version,
                endDate: moment(newVersion.startDate).subtract(1, 'days').toDate()
              }
              versionsToMod.push({ version: modVersion, method: 'PATCH' })
              const modifiedVersion = { ...version, endDate: modVersion.endDate }
              return modifiedVersion

              // Case 5: Version "i" starts between new Version dates and ends after new version endDate.
              // Version "i" startDate will be after new Version endDate.
            } else if (
              moment(version.startDate).isBetween(
                moment(newVersion.startDate),
                moment(newVersion.endDate),
                'days',
                '[]'
              ) &&
              version.endDate &&
              moment(version.endDate).isAfter(moment(newVersion.endDate))
            ) {
              const modVersion = {
                id: version.id,
                version: version.version,
                startDate: moment(newVersion.endDate).add(1, 'days').toDate()
              }
              versionsToMod.push({ version: modVersion, method: 'PATCH' })
              return { ...version, ...modVersion }
            }
            // Case 6: Version "i" starts and ends between new Version dates --> Version "i" must be deleted.
            else if (
              version.endDate &&
              moment(version.startDate).isSameOrAfter(moment(newVersion.startDate), 'day') &&
              moment(version.endDate).isSameOrBefore(moment(newVersion.endDate), 'day')
            ) {
              versionsToMod.push({ version: { id: version.id }, method: 'DELETE' })
              return
            }
          }
        } else {
          // New version remains the same as user has entered.
          if (newVersion.id) {
            versionsToMod.push({ version: editedProps, method: 'PATCH' })
            return newVersion
          } else {
            const addedVersion = { ...newVersion, id: Math.random() }
            versionsToMod.push({ version: addedVersion, method: 'POST' })
            setVersionsValidation(versionsValidation => [
              ...versionsValidation,
              { id: addedVersion.id, isValid: true }
            ])
            return addedVersion
          }
        }
      })

      const newList = list.filter(el => el?.version)

      const versionsToModify = versionsToMod
      const sortedVersions = sortVersionsByStartDate(newList).map((version, idx) => {
        // if version's version does not match the already sorted by date array, then we need to fix the versions.
        if (version.version !== idx + 1) {
          // we need to fix the version number to be equal idx+1.
          // 2 options: either the version is already set to be modified (created by user o modified, without saving) or not.
          // If the version is in versionsToMod list, we edit that obj. Otherwise, we add a new obj to patch the version number.
          const versionIsInModList = versionsToModify.some(el => el.version.id === version.id)
          if (versionIsInModList) {
            // we edit versionsToMod list to update the obj.
            versionsToMod = versionsToMod.map(vers => {
              if (vers.version.id === version.id) {
                return {
                  version: { ...vers.version, version: idx + 1 },
                  method: vers.method
                }
              } else {
                return vers
              }
            })
          } else {
            // if the item its not in the list, we add it.
            versionsToMod.push({
              version: { id: version.id, version: idx + 1 },
              method: 'PATCH'
            })
          }
        }
        return { ...version, version: idx + 1 }
      })

      let versionsId: string[] = []
      versionsToMod = versionsToMod.filter(el => {
        const isInList = versionsId.includes(el.version.id)
        if (isInList) {
          return false
        } else {
          versionsId.push(el.version.id)
          return true
        }
      })

      return [sortedVersions, versionsToMod]
    } else {
      // SCENARIOS WHERE NEW VERSION END DATE IS NULL/UNDEFINED (INFINITE)
      const list = versionsList.map(version => {
        if (version.id === newVersion.id) {
          // New version remains the same as user has entered.
          if (newVersion.id) {
            versionsToMod.push({ version: newVersion, method: 'PATCH' })
            return newVersion
          } else {
            const addedVersion = { ...newVersion, id: Math.random() }
            versionsToMod.push({ version: addedVersion, method: 'POST' })
            setVersionsValidation(versionsValidation => [
              ...versionsValidation,
              { id: addedVersion.id, isValid: true }
            ])
            return addedVersion
          }
        }
        if (!version.endDate) {
          // CASES WITH VERSION END DATE NULL AS WELL !!! New Version infinite endDate has priority.
          // Case 1: Version "i" starts before new Version startDate --> Version "i" endDate will be new Version startDate.
          if (moment(version.startDate).isBefore(moment(newVersion.startDate), 'day')) {
            const modVersion = {
              id: version.id,
              version: version.version,
              endDate: moment(newVersion.startDate).subtract(1, 'days').toDate()
            }
            versionsToMod.push({ version: modVersion, method: 'PATCH' })

            const modifiedVersion = { ...version, endDate: modVersion.endDate }
            return modifiedVersion
          }
          // Case 2: Version "i" starts AFTER new Version startDate --> Version "i" must be deleted.
          else if (moment(version.startDate).isSameOrAfter(moment(newVersion.startDate), 'day')) {
            versionsToMod.push({ version: { id: version.id }, method: 'DELETE' })
            return
          }
        } else {
          // Cases with Version end date FINITE and new Version endDate INFINITE.
          // Case 1: Version "i" ends before new Version starts --> Version "i" remains the same.
          if (
            version.endDate &&
            moment(version.endDate).isBefore(moment(newVersion.startDate), 'day')
          ) {
            return version
            // Case 2: Version "i" starts after new Version startDate --> Version "i" must be deleted.
          } else if (moment(version.startDate).isSameOrAfter(moment(newVersion.startDate), 'day')) {
            // updateModifiedVersions(version, 'DELETE');
            versionsToMod.push({ version: { id: version.id }, method: 'DELETE' })
            return
            // Case 3: Version "i" starts before newVersion startDate and ends after (or same) newVersion startDate.
          } else if (
            version.endDate &&
            moment(version.startDate).isBefore(moment(newVersion.startDate), 'day') &&
            moment(version.endDate).isSameOrAfter(moment(newVersion.startDate), 'day')
          ) {
            const modVersion = {
              id: version.id,
              version: version.version,
              endDate: moment(newVersion.startDate).subtract(1, 'days').toDate()
            }
            // updateModifiedVersions(modVersion, 'PATCH');
            versionsToMod.push({ version: modVersion, method: 'PATCH' })
            const modifiedVersion = { ...version, endDate: modVersion.endDate }
            return modifiedVersion
          }
        }
      })

      const newList = list.filter(el => el?.version)
      const versionsToModify = versionsToMod
      const sortedVersions = sortVersionsByStartDate(newList).map((version, idx) => {
        // if version's version does not match the already sorted by date array, then we need to fix the versions.
        if (version.version !== idx + 1) {
          // we need to fix the version number to be equal idx+1.
          // 2 options: either the version is already set to be modified (created by user o modified, without saving) or not.
          // If the version is in versionsToMod list, we edit that obj. Otherwise, we add a new obj to patch the version number.
          const versionIsInModList = versionsToModify.some(el => el.version.id === version.id)
          if (versionIsInModList) {
            // we edit versionsToMod list to update the obj.
            versionsToMod = versionsToMod.map(vers => {
              if (vers.version.id === version.id) {
                return {
                  version: { ...vers.version, version: idx + 1 },
                  method: vers.method
                }
              } else {
                return vers
              }
            })
          } else {
            // if the item its not in the list, we add it.
            versionsToMod.push({
              version: { id: version.id, version: idx + 1 },
              method: 'PATCH'
            })
          }
        }
        return { ...version, version: idx + 1 }
      })

      let versionsId: string[] = []
      versionsToMod = versionsToMod.filter(el => {
        const isInList = versionsId.includes(el.version.id)
        if (isInList) {
          return false
        } else {
          versionsId.push(el.version.id)
          return true
        }
      })

      return [sortedVersions, versionsToMod]
    }
  }

  const getPriceVersions = async (forceRefresh?: boolean) => {
    if (price) {
      setIsFetching(true)
      const persistedVersions = getVersions()
      if (persistedVersions && !forceRefresh) {
        setIsFetching(false)
        setVersions(persistedVersions)
        setModifiedVersions([])
      } else {
        await fetchPriceVersions(price.id).then(response => {
          setIsFetching(false)
          setVersions([])
          setModifiedVersions([])
          if (response.success) {
            const sortedByDateVersions = sortPriceVersionsByStartDate(response.data)
            setVersions(sortedByDateVersions)
            storeVersions(sortedByDateVersions)
          } else {
            setIsFetching(false)
            // response.error &&
            //   notifyError(
            //     t('pricingDetails:notifications.loadPriceValues.error', {
            //       error: response.error.code
            //     })
            //   );
          }
        })
      }
    }
  }

  const savePriceVersions = async () => {
    const isGapOnDates = versions.some((ver, idx) => {
      const nextVersion = idx + 1 === versions.length ? null : versions[idx + 1]
      const isGapNext = nextVersion
        ? moment(ver.endDate).diff(moment(nextVersion.startDate), 'days') !== -1
        : false
      return isGapNext
    })
    if (!isGapOnDates && modifiedVersions && price) {
      setIsFetching(true)
      await saveVersionsBatch(modifiedVersions, price?.id).then(response => {
        setIsFetching(false)
        setVersions([])
        setModifiedVersions([])
        if (response.success) {
          getPriceVersions(true)
          setVersionsValidation([])
          // recalculateValues();
          info('Se han actualizado las versiones con éxito.')
        } else {
          setIsFetching(false)
          console.log(response)
          // response.error &&
          notifyError(t('Hubo un error al intentar guardar los cambios.'))
        }
      })
    } else {
      notifyError(
        'Las fechas de las versiones deben ser continuas. Modifíquelas e intente nuevamente.'
      )
    }
  }

  // Functions Region Ends

  // Hooks Region Starts

  useEffect(() => {
    getPriceVersions()
  }, [price])

  // Hooks Region Ends

  return (
    <>
      <Page notification={globalNotification} layout={PageLayout.MULTI_COLUMN} error={errorProps}>
        <div className={classes.container}>
          <div className={classes.leftPanel}>
            {price ? (
              <>
                <Typography variant="h1" color="primary" noWrap={true}>
                  {price && t('versions:title', { priceCode: price.code })}
                </Typography>
                <List className={classes.resume}>
                  <ListItem key={`${price.id}-desc`} disableGutters>
                    <Typography variant={'h2'}>{price?.description}</Typography>
                  </ListItem>
                  <ListItem key={`${price.id}-type`} divider disableGutters>
                    <Typography variant={'caption'}>
                      {t(`versions:leftColumn.captions.type`)}
                    </Typography>
                    <Typography variant={'subtitle2'}>
                      {price &&
                        t(`enums:PriceTypes.${enumKeyToValue(EnumPriceType, price.priceType)}`)}
                    </Typography>
                  </ListItem>
                </List>
              </>
            ) : (
              <FormSkeleton withTitle={true} elevation={0} columns={1} inputs={9}></FormSkeleton>
            )}
          </div>
          <Divider
            className={classes.divider}
            orientation={matchesSmallDevices ? 'horizontal' : 'vertical'}
            variant="fullWidth"
          />
          <div className={classes.rightPanel}>
            <div className={classes.headerContainer}>
              <Grid container direction="row" justify="space-between">
                <Typography variant="h1" color="secondary" noWrap={true}>
                  Versiones
                </Typography>
                <Grid
                  item
                  style={{
                    marginLeft: 'auto',
                    marginBottom: currentTheme.spacing(2)
                  }}
                >
                  <Button onClick={() => goBackHandler()} size="small" color="primary">
                    {t('versions:mainActions.back')}
                  </Button>
                </Grid>
                <Grid item style={{ marginLeft: currentTheme.spacing(2) }}>
                  <Button
                    onClick={() => discardChangesHandler()}
                    size="small"
                    color="primary"
                    disabled={modifiedVersions && modifiedVersions?.length === 0}
                  >
                    {t('versions:mainActions.discardChanges')}
                  </Button>
                </Grid>
                <Grid item style={{ marginLeft: currentTheme.spacing(2) }}>
                  <Button
                    onClick={() => saveVersionsHandler()}
                    size="small"
                    color="primary"
                    variant="contained"
                    disabled={
                      !(
                        !versionsValidation.some(el => el.isValid === false) &&
                        modifiedVersions &&
                        modifiedVersions?.length !== 0
                      )
                    }
                  >
                    {t('versions:mainActions.save')}
                  </Button>
                </Grid>
              </Grid>
            </div>
            <Grid container item direction="row" justify="space-between">
              <Fab
                variant="extended"
                className={classes.fabExtended}
                color="default"
                size="medium"
                onClick={() => setShowAddVersionDialog(true)}
              >
                <AddIcon color="primary" className={classes.fabIcon} />
                <span className={classes.fabLabel}>{t('versions:mainActions.new')}</span>
              </Fab>
            </Grid>
            {!isFetching && versions.length > 0 ? (
              <Grid container direction="column" style={{ marginTop: currentTheme.spacing(3) }}>
                {versions && price ? (
                  versions.map((version, idx) => {
                    const activeVersion = versions
                      .filter(version => version.version === price.version)
                      .map(vs => vs.version)[0]

                    const canDelete = versions.length === 1 ? false : true

                    return (
                      <VersionCard
                        key={`${version.id}-${idx}${price.code}${version.version}`}
                        priceVersion={version}
                        price={price}
                        defaultExpanded={activeVersion === version.version}
                        expanded={version.version === expandedCard}
                        onHandleChange={handleChange}
                        canDelete={canDelete}
                        onIsValidated={updateVersionsValidationList}
                        onRefreshPage={() => {
                          getPriceVersions()
                        }}
                        onDeleteVersion={() => deleteVersionHandler(version)}
                        onEditVersion={modifVersion => editVersionHandler(modifVersion, version)}
                        firstVersionStartDate={
                          versions.length > 0 && versions[0].startDate
                            ? versions[0].startDate
                            : null
                        }
                        unMountOnExit={versions.length > 10 ? true : false}
                        versions={versions}
                      />
                    )
                  })
                ) : (
                  <FormSkeleton
                    withTitle={true}
                    elevation={0}
                    columns={1}
                    inputs={9}
                  ></FormSkeleton>
                )}
              </Grid>
            ) : (
              <div style={{ marginTop: currentTheme.spacing(2) }}>
                <FormSkeleton withTitle={true} elevation={0} columns={1} inputs={9} />
              </div>
            )}
          </div>
        </div>

        <AddVersionDialog
          show={showAddVersionDialog}
          newVersionNumber={versions.length + 1}
          onShowAddVersionDialog={show => setShowAddVersionDialog(show)}
          onRefreshPage={() => {
            getPriceVersions()
          }}
          onAddVersion={newVersion => addVersionHandler(newVersion)}
          firstVersionStartDate={
            versions.length > 0 && versions[0].startDate ? versions[0].startDate : null
          }
          versions={versions}
        />

        <ErrorDetailList {...errorDetailListDialogProps}></ErrorDetailList>
      </Page>
    </>
  )
}
